import axios from '@/util/axios.config'

let socket = null
let reconnectAttempts = 0
const maxReconnectAttempts = 5
let reconnectTimer = null
let messageHandlers = new Map() // 存储消息处理器

/**
 * 初始化原生 WebSocket 连接
 * @param {string} token - JWT token
 * @returns {WebSocket} WebSocket 实例
 */
export function initSocket(token) {
  // 如果已连接，直接返回
  if (socket && socket.readyState === WebSocket.OPEN) {
    return socket
  }

  // 如果正在连接，等待
  if (socket && socket.readyState === WebSocket.CONNECTING) {
    return socket
  }

  // 获取基础URL
  let baseURL = process.env.VUE_APP_API_BASE_URL || process.env.VUE_APP_WS_URL
  
  if (!baseURL) {
    // 从 axios 配置获取 baseURL（如果有）
    baseURL = axios.defaults.baseURL
    
    // 如果 axios 也没有 baseURL，根据当前环境判断
    if (!baseURL) {
      const hostname = window.location.hostname
      
      // 如果是开发环境（localhost 或 127.0.0.1），使用代理目标地址
      if (hostname === 'localhost' || hostname === '127.0.0.1') {
        baseURL = 'http://jxnujwc.top:30000'
      } else {
        // 生产环境，使用当前域名
        baseURL = window.location.origin
      }
    }
    
    // 如果 baseURL 包含路径，只保留协议和主机部分
    try {
      if (baseURL.startsWith('/')) {
        baseURL = window.location.origin + baseURL
      } else if (!baseURL.startsWith('http://') && !baseURL.startsWith('https://')) {
        baseURL = window.location.origin
      }
      
      const url = new URL(baseURL)
      // 转换为 WebSocket 协议
      const wsProtocol = url.protocol === 'https:' ? 'wss:' : 'ws:'
      baseURL = `${wsProtocol}//${url.host}`
    } catch (e) {
      // 如果解析失败，根据当前页面协议判断
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
      baseURL = `${protocol}//${window.location.hostname}${window.location.port ? ':' + window.location.port : ''}`
      console.warn('[WebSocket] URL 解析失败，使用当前页面地址:', baseURL)
    }
  } else {
    // 如果 baseURL 是 http/https，转换为 ws/wss
    try {
      const url = new URL(baseURL)
      const wsProtocol = url.protocol === 'https:' ? 'wss:' : 'ws:'
      baseURL = `${wsProtocol}//${url.host}`
    } catch (e) {
      console.warn('[WebSocket] URL 转换失败:', e)
      // 降级方案：使用当前页面地址
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
      baseURL = `${protocol}//${window.location.hostname}${window.location.port ? ':' + window.location.port : ''}`
    }
  }
  
  // 构建 WebSocket URL，添加 token
  const wsUrl = `${baseURL}/ws?token=${encodeURIComponent(token)}`
  
  console.log('[WebSocket] 准备连接到:', wsUrl)
  console.log('[WebSocket] Token 存在:', !!token)
  
  try {
    socket = new WebSocket(wsUrl)
    
    // 连接成功
    socket.onopen = () => {
      console.log('[WebSocket] 连接成功')
      reconnectAttempts = 0
      
      // 触发 connect 事件
      triggerEvent('connect', {})
    }
    
    // 接收消息
    socket.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data)
        const { type, data: messageData } = data
        
        console.log('[WebSocket] 收到消息:', type, messageData)
        
        // 触发对应类型的事件
        triggerEvent(type, messageData)
      } catch (error) {
        console.error('[WebSocket] 消息解析失败:', error, event.data)
      }
    }
    
    // 连接错误
    socket.onerror = (error) => {
      console.error('[WebSocket] 连接错误:', error)
      triggerEvent('error', { message: 'WebSocket 连接错误' })
    }
    
    // 连接关闭
    socket.onclose = (event) => {
      console.log('[WebSocket] 连接关闭，代码:', event.code, '原因:', event.reason)
      
      triggerEvent('disconnect', { reason: event.reason || '连接关闭' })
      
      // 如果不是正常关闭，尝试重连
      if (event.code !== 1000 && reconnectAttempts < maxReconnectAttempts) {
        reconnectAttempts++
        const delay = Math.min(1000 * Math.pow(2, reconnectAttempts - 1), 5000)
        console.log(`[WebSocket] ${delay}ms 后尝试重连 (${reconnectAttempts}/${maxReconnectAttempts})`)
        
        reconnectTimer = setTimeout(() => {
          if (token) {
            initSocket(token)
          }
        }, delay)
      } else if (reconnectAttempts >= maxReconnectAttempts) {
        console.error('[WebSocket] 重连次数已达上限，停止重连')
        triggerEvent('connect_error', { message: '重连次数已达上限' })
      }
    }
    
  } catch (error) {
    console.error('[WebSocket] 创建连接失败:', error)
    triggerEvent('connect_error', { message: error.message })
  }

  return socket
}

/**
 * 触发事件（模拟 Socket.IO 的事件系统）
 */
function triggerEvent(type, data) {
  const handlers = messageHandlers.get(type) || []
  handlers.forEach(handler => {
    try {
      handler(data)
    } catch (error) {
      console.error(`[WebSocket] 事件处理器错误 (${type}):`, error)
    }
  })
  
  // 当收到 'connected' 消息时，同时触发 'connect' 事件（兼容 Socket.IO 的事件名）
  if (type === 'connected') {
    const connectHandlers = messageHandlers.get('connect') || []
    connectHandlers.forEach(handler => {
      try {
        handler({})
      } catch (error) {
        console.error(`[WebSocket] connect 事件处理器错误:`, error)
      }
    })
  }
}

/**
 * 监听消息（模拟 Socket.IO 的 on 方法）
 */
export function onMessage(type, handler) {
  if (!messageHandlers.has(type)) {
    messageHandlers.set(type, [])
  }
  messageHandlers.get(type).push(handler)
  
  // 返回取消监听的函数
  return () => {
    const handlers = messageHandlers.get(type)
    if (handlers) {
      const index = handlers.indexOf(handler)
      if (index > -1) {
        handlers.splice(index, 1)
      }
    }
  }
}

/**
 * 发送消息（模拟 Socket.IO 的 emit 方法）
 */
export function emit(type, payload) {
  if (!socket || socket.readyState !== WebSocket.OPEN) {
    console.warn('[WebSocket] 连接未建立，无法发送消息')
    return false
  }
  
  try {
    const message = JSON.stringify({
      type,
      payload
    })
    
    socket.send(message)
    console.log('[WebSocket] 发送消息:', type, payload)
    return true
  } catch (error) {
    console.error('[WebSocket] 发送消息失败:', error)
    return false
  }
}

/**
 * 获取 Socket 实例
 */
export function getSocket() {
  return socket
}

/**
 * 断开 WebSocket 连接
 */
export function disconnectSocket() {
  if (reconnectTimer) {
    clearTimeout(reconnectTimer)
    reconnectTimer = null
  }
  
  if (socket) {
    socket.close(1000, '主动断开')
    socket = null
  }
  
  // 清空所有事件处理器
  messageHandlers.clear()
}

/**
 * 检查是否已连接
 */
export function isConnected() {
  return socket && socket.readyState === WebSocket.OPEN
}

/**
 * 重新连接
 */
export function reconnect(token) {
  disconnectSocket()
  reconnectAttempts = 0
  return initSocket(token)
}

